/**
 * Canal 2 del modelo de coordinación del inbox: presencia viva por
 * conversación — qué agentes están MIRANDO cada conversación ahora mismo.
 *
 * Contrato: spec 2026-07-09-inbox-coordination-states-design.md. Dos fuentes
 * que convergen acá:
 *  - Snapshot `ConversationResponse.presence` (viewers al momento del fetch,
 *    viene del hub WS del backend) — SIEMBRA/reemplaza el set por conversación.
 *  - Eventos Socket.IO tenant-wide `inbox:agent_viewing` /
 *    `inbox:agent_left_viewing` — suman/restan viewers en vivo.
 *
 * El typing (🟢 "escribiendo") NO vive acá: ya existe en
 * `@/lib/realtime/typing-store` (lane agente, TTL 4s). Este store solo lleva
 * viewers; `derivePresenceBadge` combina ambos con la prioridad del spec.
 *
 * Mismo patrón que typing-store: Maps mutados in-place + token `tick` para
 * que los componentes se suscriban a cambios sin clonar estructuras por
 * evento.
 */
import { create } from 'zustand';

export interface ConversationViewer {
  userId: string;
  name: string;
  avatar?: string;
}

interface InboxPresenceState {
  /** conversationId → userId → viewer. Map-of-Maps: altas/bajas O(1). */
  viewersByConversation: Map<string, Map<string, ConversationViewer>>;
  /** Token de suscripción (ver typing-store para el racional). */
  tick: number;
  /**
   * Reemplaza el set de viewers de una conversación con el snapshot del
   * server (autoritativo al momento del fetch). `selfId` se excluye
   * idempotentemente — el backend ya excluye al emisor de los broadcasts,
   * pero el snapshot REST sí puede incluirnos y el propio agente no debe
   * contarse como "otro viendo esto".
   */
  seedSnapshot(
    conversationId: string,
    viewers: ConversationViewer[] | undefined,
    selfId: string | undefined,
  ): void;
  /** Alta de viewer por evento en vivo (ignora self — idempotencia). */
  addViewer(
    conversationId: string,
    viewer: ConversationViewer,
    selfId: string | undefined,
  ): void;
  /** Baja de viewer por evento en vivo. */
  removeViewer(conversationId: string, userId: string): void;
  /** Lectura no-reactiva (para render tras suscribirse a `tick`). */
  viewersFor(conversationId: string): ConversationViewer[];
  /** Logout/disconnect: la presencia es efímera, no debe sobrevivir sesión. */
  clear(): void;
}

export const useInboxPresenceStore = create<InboxPresenceState>((set, get) => ({
  viewersByConversation: new Map(),
  tick: 0,

  seedSnapshot: (conversationId, viewers, selfId) => {
    set((state) => {
      const others = (viewers ?? []).filter((v) => v.userId && v.userId !== selfId);
      const prev = state.viewersByConversation.get(conversationId);
      // No-op guard: los list-fetch siembran N filas por página; si el set no
      // cambió (caso común: nadie mirando antes ni ahora) no bumpeamos tick.
      if (!prev && others.length === 0) return {};
      if (
        prev &&
        prev.size === others.length &&
        // Igualdad por CONTENIDO, no solo por membership: el snapshot es
        // autoritativo también para name/avatar (un agente puede renombrarse
        // o cambiar foto entre fetches) — con solo userIds el guard dejaría
        // esos datos stale.
        others.every((v) => {
          const cur = prev.get(v.userId);
          return !!cur && cur.name === v.name && cur.avatar === v.avatar;
        })
      ) {
        return {};
      }
      if (others.length === 0) {
        state.viewersByConversation.delete(conversationId);
      } else {
        state.viewersByConversation.set(
          conversationId,
          new Map(others.map((v) => [v.userId, v])),
        );
      }
      return { tick: state.tick + 1 };
    });
  },

  addViewer: (conversationId, viewer, selfId) => {
    if (!viewer.userId || viewer.userId === selfId) return;
    set((state) => {
      const conv =
        state.viewersByConversation.get(conversationId) ??
        new Map<string, ConversationViewer>();
      conv.set(viewer.userId, viewer);
      state.viewersByConversation.set(conversationId, conv);
      return { tick: state.tick + 1 };
    });
  },

  removeViewer: (conversationId, userId) => {
    set((state) => {
      const conv = state.viewersByConversation.get(conversationId);
      if (!conv?.has(userId)) return {};
      conv.delete(userId);
      if (conv.size === 0) state.viewersByConversation.delete(conversationId);
      return { tick: state.tick + 1 };
    });
  },

  viewersFor: (conversationId) => {
    const conv = get().viewersByConversation.get(conversationId);
    return conv ? Array.from(conv.values()) : [];
  },

  clear: () => {
    set((state) =>
      state.viewersByConversation.size === 0
        ? {}
        : { viewersByConversation: new Map(), tick: state.tick + 1 },
    );
  },
}));

/** Badge de presencia de una fila (Canal 2), prioridad del spec. */
export type PresenceBadge =
  | { kind: 'collision'; count: number }
  | { kind: 'typing'; name: string }
  | { kind: 'viewing'; viewer: ConversationViewer };

/**
 * Deriva el overlay de la fila combinando viewers (este store) y typers
 * (typing-store, lane agente). Prioridad: colisión > escribiendo > viendo.
 * Puro para poder testearlo sin montar stores.
 */
export function derivePresenceBadge(
  viewers: ConversationViewer[],
  typers: Array<{ userId: string; userName: string }>,
): PresenceBadge | null {
  if (viewers.length >= 2) return { kind: 'collision', count: viewers.length };
  if (typers.length > 0) return { kind: 'typing', name: typers[0].userName };
  if (viewers.length === 1) return { kind: 'viewing', viewer: viewers[0] };
  return null;
}

/**
 * Selector hook-friendly (mismo trade-off que `useTypingUsersFor`): se
 * suscribe a `tick` y lee el Map en render. Para listas, preferí suscribirte
 * UNA vez en el componente contenedor y leer por fila con `viewersFor`.
 */
export function useConversationViewers(
  conversationId: string | null | undefined,
): ConversationViewer[] {
  const tick = useInboxPresenceStore((s) => s.tick);
  void tick;
  if (!conversationId) return [];
  return useInboxPresenceStore.getState().viewersFor(conversationId);
}
